import {
  Children,
  Fragment,
  isValidElement,
  type ChangeEvent,
  type ReactElement,
  type ReactNode,
  type SelectHTMLAttributes,
} from "react";
import {
  SearchableSelect,
  searchThreshold,
  type SelectOption,
} from "./SearchableSelect";

/** Plain text of option content (strings, numbers, nested elements). */
function text(node: ReactNode): string {
  if (node === null || node === undefined || typeof node === "boolean")
    return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(text).join("");
  if (isValidElement(node))
    return text((node as ReactElement<{ children?: ReactNode }>).props.children);
  return "";
}
type OptionProps = {
  value?: string | number;
  disabled?: boolean;
  children?: ReactNode;
  label?: string;
};
/** Flattens <option>/<optgroup>/fragments into searchable options. */
export function selectOptions(children: ReactNode, group?: string) {
  const options: (SelectOption & { group?: string })[] = [];
  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;
    if (child.type === Fragment) {
      options.push(
        ...selectOptions(
          (child as ReactElement<{ children?: ReactNode }>).props.children,
          group,
        ),
      );
      return;
    }
    if (child.type === "optgroup") {
      const props = (child as ReactElement<OptionProps>).props;
      options.push(...selectOptions(props.children, props.label || group));
      return;
    }
    if (child.type !== "option") return;
    const props = (child as ReactElement<OptionProps>).props;
    const label = text(props.children) || props.label || "";
    options.push({
      value: String(props.value ?? label),
      label,
      disabled: props.disabled,
      ...(group ? { detail: group, group } : {}),
    });
  });
  return options;
}

type Props = Omit<SelectHTMLAttributes<HTMLSelectElement>, "onChange"> & {
  onChange?: (event: ChangeEvent<HTMLSelectElement>) => void;
  /** Force the native control (for example inside a compact toolbar). */
  native?: boolean;
};

/**
 * The application's one dropdown. It takes the same props and <option>
 * children as a native <select>: lists under five options stay native, longer
 * lists become the searchable dropdown (search inside the opened list, focused
 * on open). Change handlers receive an event-like object with target.value.
 */
export function Select(props: Props) {
  const {
    children,
    value,
    defaultValue,
    onChange,
    disabled,
    id,
    className,
    title,
    multiple,
    native,
    ...rest
  } = props;
  const all = selectOptions(children);
  const empty = all.find((option) => option.value === "");
  const options = all.filter((option) => option !== empty);
  if (native || multiple || options.length < searchThreshold)
    return (
      <select
        {...rest}
        id={id}
        className={className}
        title={title}
        multiple={multiple}
        disabled={disabled}
        value={value}
        defaultValue={defaultValue}
        onChange={onChange}
      >
        {children}
      </select>
    );
  const current = String(value ?? defaultValue ?? "");
  const label = (rest["aria-label"] as string | undefined) || title;
  return (
    <SearchableSelect
      id={id}
      className={className}
      label={label}
      nativeOptions={children}
      triggerProps={{ ...rest, title }}
      value={current}
      disabled={disabled}
      options={options}
      emptyOption={empty?.label}
      placeholder={empty?.label || "Choose…"}
      onChange={(next) => {
        if (!onChange) return;
        const target = { value: next, name: rest.name } as HTMLSelectElement;
        onChange({
          target,
          currentTarget: target,
          preventDefault() {},
          stopPropagation() {},
        } as unknown as ChangeEvent<HTMLSelectElement>);
      }}
    />
  );
}
